import { useMutation } from '@tanstack/react-query';

import { MatchesPlayers, SubPlayerData } from '@/modules/league/new-components/types';

async function saveSubPlayer({
  matchDateId,
  player,
  subbedPlayer,
}: {
  matchDateId: string;
  player: SubPlayerData;
  subbedPlayer: number;
}) {
  try {
    const response = await fetch(`/api/v1/leagues/matches/subPlayer`, {
      method: 'POST',
      body: JSON.stringify({
        ScoreID: player.ScoreID,
        MatchDateID: matchDateId,
        PoolID: player.PoolID,
        SessionTeamID_Out: player.SessionTeamID_Out,
        UserID_Out: player.UserID_Out,
        doing: subbedPlayer,
      }),
    });
    const responseData = await response.json();
    return responseData.data;
  } catch {
    /* empty */
  }
}

async function saveMatchScores({
  player,
  mutationData,
}: {
  player: MatchesPlayers;
  mutationData: {
    [key: string]: number;
  };
}) {
  try {
    const response = await fetch(`/api/v1/leagues/matches/saveMatchScores`, {
      method: 'POST',
      body: JSON.stringify({
        ...mutationData,

        ScoreID: player.ScoreID,
      }),
    });
    const responseData = await response.json();
    return responseData?.data;
  } catch {
    return { message: 'An error occurred during the Saving scores process.' };
  }
}

async function saveMatchCancel({ scoreId }: { scoreId: string }) {
  try {
    const response = await fetch(`/api/v1/leagues/matches/cancelMatch`, {
      method: 'POST',
      body: JSON.stringify({
        ScoreID: scoreId,
      }),
    });
    const responseData = await response.json();
    return responseData.data;
  } catch {
    /* empty */
  }
}

async function saveMatchUncancel({ scoreId }: { scoreId: string }) {
  try {
    const response = await fetch(`/api/v1/leagues/matches/uncancelMatch`, {
      method: 'POST',
      body: JSON.stringify({
        ScoreID: scoreId,
      }),
    });
    const responseData = await response.json();
    return responseData.data;
  } catch {
    /* empty */
  }
}

async function removeRatingPlayer({
  matchDateId,
  sequenceNumber,
  fullName,
  jsonPost,
}: {
  matchDateId: string;
  sequenceNumber: number;
  fullName: string;
  jsonPost: { SessionTeamID: string; IsChecked: number }[];
}) {
  try {
    const response = await fetch(`/api/v1/leagues/matches/removeRatingPlayer`, {
      method: 'POST',
      body: JSON.stringify({
        matchDateId: matchDateId,
        sequenceNumber: sequenceNumber,
        fullName: fullName,
        jsonPost: jsonPost,
      }),
    });
    const responseData = await response.json();
    return responseData.data;
  } catch {
    /* empty */
  }
}

async function getMatchInfosOnScoreSheets({
  matchDateId,
  selectedPoolID,
  sequenceNumber,
}: {
  matchDateId: string;
  selectedPoolID: string;
  sequenceNumber: number;
}) {
  const response = await fetch(`/api/v1/leagues/matches/matchesInfosOnScoreSheets`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      matchDateId,
      selectedPoolID,
      sequenceNumber,
    }),
  });
  const responseData = await response.json();
  if (!response.ok) throw new Error(responseData.error || 'Failed to fetch data');
  return responseData.data.matchesInfos;
}

async function saveChangeScoreFormat({
  matchDateId,
  scoreId,
  poolId,
  scoreFormatId,
}: {
  matchDateId: string;
  scoreId?: string;
  poolId: string;
  scoreFormatId: number;
}) {
  try {
    const response = await fetch(`/api/v1/leagues/gameDay/header/changeScoreFormat`, {
      method: 'POST',
      body: JSON.stringify({
        ScoreID: scoreId,
        MatchDateID: matchDateId,
        PoolID: poolId,
        ScoreFormatID: scoreFormatId,
      }),
    });
    const responseData = await response.json();
    return responseData.data;
  } catch {
    /* empty */
  }
}

export const useSaveMatchCancel = () => useMutation(saveMatchCancel);
export const useSaveMatchUncancel = () => useMutation(saveMatchUncancel);
export const useSubPlayer = () => useMutation(saveSubPlayer);
export const useSaveMatchScores = () => useMutation(saveMatchScores);
export const useRemoveRatingPlayer = () => useMutation(removeRatingPlayer);
export const useGetMatchInfosOnScoreSheets = () => useMutation(getMatchInfosOnScoreSheets);
export const useSaveChangeScoreFormat = () => useMutation(saveChangeScoreFormat);
